import type { Extra, Tags } from '../../../shared/lib/ClientLog.no-deps'
import type { CatchError } from '../../../shared/lib/functional/error.no-deps'

/* wrappers around localStorage methods with catch+log functionality */

export type ErrorLogger = (maybeError: CatchError, extra?: Extra, tags?: Tags) => void

export function safeGetItem<T>(
    localStorageKey: string,
    logError: ErrorLogger,
    localStorage: Pick<Storage, 'getItem'> = window.localStorage
): T {
    let str
    try {
        // Retrieve
        str = localStorage.getItem(localStorageKey)
        if (str == null || str === 'undefined') {
            return null
        }

        // Deserialize
        const value = JSON.parse(str)

        return value
    } catch (error) {
        logError(error, { localStorageKey, str })
        return null
    }
}

export function safeSetItem<T>(
    localStorageKey: string,
    value: T,
    logError: ErrorLogger,
    localStorage: Pick<Storage, 'setItem'> = window.localStorage
) {
    try {
        // Serialize
        const str = JSON.stringify(value)

        // Persist
        localStorage.setItem(localStorageKey, str)
    } catch (error) {
        const errStr = error.toString()

        // Quota exceeded
        if (
            errStr.includes('QuotaExceededError') ||
            errStr.includes('QUOTA_EXCEEDED_ERR') ||
            error.code === 22 ||
            error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
            error.number === -2147024882
        ) {
            logError('localStorage quota exceeded', { localStorageKey, value })
            return
        }

        logError(error)
    }
}

export function safeRemoveItem(
    localStorageKey: string,
    logError: ErrorLogger,
    localStorage: Pick<Storage, 'removeItem'> = window.localStorage
) {
    try {
        localStorage.removeItem(localStorageKey)
    } catch (error) {
        logError(error, { localStorageKey })
    }
}
